import React, { useEffect, useRef, useState } from 'react';
import { PageHeader } from './PageHeader';
import { Icon, iconsBySet, ICON_SETS, type IconName, type IconSet } from '../../../design-system/icons';

/**
 * Components › Icons — traslado del frame "Icons" de Figma (node 22-523).
 * Galería de los íconos del DS por set (outline / solid / color), renderizada
 * desde el registro `ICONS`. Los mismos íconos se consumen en la app vía `<Icon>`.
 *
 * La grilla es un CSS grid de celdas 80px que envuelve a una fila nueva pasadas
 * `MAX_COLS`. Para que el borde cierre parejo (sin líneas dobles ni filas abiertas),
 * los bordes EXTERNOS los pone el contenedor y los INTERNOS las celdas (derecha salvo
 * última columna, abajo salvo última fila); la última fila se completa con celdas de
 * relleno vacías, igual que el frame de Figma.
 */

/** Delay antes de mostrar el nombre del ícono al hacer hover. */
const HOVER_DELAY_MS = 500;

/** Máximo de columnas por fila (celdas de 80px) antes de envolver. */
const MAX_COLS = 9;

const IconCell: React.FC<{ name: IconName; className?: string }> = ({ name, className }) => {
  const [showName, setShowName] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const handleEnter = () => {
    timer.current = setTimeout(() => setShowName(true), HOVER_DELAY_MS);
  };
  const handleLeave = () => {
    if (timer.current) clearTimeout(timer.current);
    setShowName(false);
  };
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  return (
    <div
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className={`relative flex size-20 cursor-pointer items-center justify-center border-[#ededed] transition-colors duration-150 hover:bg-neutral-100 ${className ?? ''}`}
    >
      <Icon name={name} className="text-neutral-900" />

      {/* Tooltip con el nombre — aparece a los 500ms de hover */}
      {showName && (
        <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-neutral-900 px-2 py-1 text-[12px] font-medium text-white shadow-md">
          {name}
          <span className="absolute left-1/2 top-full -ml-1 border-4 border-transparent border-t-neutral-900" />
        </div>
      )}
    </div>
  );
};

const IconGrid: React.FC<{ set: IconSet }> = ({ set }) => {
  const names = iconsBySet(set);
  const cols = Math.min(names.length, MAX_COLS) || 1;
  const fillers = (cols - (names.length % cols)) % cols;
  const total = names.length + fillers;
  const rows = total / cols;

  // Bordes internos de cada celda (derecha salvo última col, abajo salvo última fila).
  // Los externos los pone el contenedor → sin líneas dobles.
  const edgeClass = (i: number) =>
    [i % cols !== cols - 1 && 'border-r', i < (rows - 1) * cols && 'border-b']
      .filter(Boolean)
      .join(' ');

  // Redondeo de las 4 celdas esquina (el contenedor no clippea, para no cortar el tooltip).
  const cornerClass = (i: number) => {
    const top = i < cols;
    const bottom = i >= (rows - 1) * cols;
    const left = i % cols === 0;
    const right = i % cols === cols - 1;
    return [
      top && left && 'rounded-tl-xl',
      top && right && 'rounded-tr-xl',
      bottom && left && 'rounded-bl-xl',
      bottom && right && 'rounded-br-xl',
    ]
      .filter(Boolean)
      .join(' ');
  };

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-[14px] font-semibold capitalize text-neutral-900">{set}</h2>
      <div
        className="grid w-fit rounded-xl border border-[#ededed] bg-white"
        style={{ gridTemplateColumns: `repeat(${cols}, 80px)` }}
      >
        {names.map((name, i) => (
          <IconCell key={name} name={name} className={`${edgeClass(i)} ${cornerClass(i)}`} />
        ))}
        {Array.from({ length: fillers }).map((_, k) => {
          const i = names.length + k;
          return (
            <div
              key={`filler-${k}`}
              aria-hidden
              className={`size-20 border-[#ededed] ${edgeClass(i)} ${cornerClass(i)}`}
            />
          );
        })}
      </div>
    </section>
  );
};

export const DSIconsPage: React.FC = () => (
  <div className="px-10 py-12">
    <div className="mx-auto flex w-full max-w-[800px] flex-col gap-10">
      <PageHeader
        title="Icons"
        description="Set de íconos del Design System en tres estilos —outline, solid y color—, pensados para consumirse en la aplicación. Los outline y solid se pintan con currentColor (heredan el color del texto); los de color conservan sus colores propios. Todos escalan a cualquier tamaño."
      />

      <p className="-mt-4 text-[15px] text-neutral-600">
        Consumí cualquier ícono con{' '}
        <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[13px] text-neutral-700">
          {'<Icon name="outline/user" />'}
        </code>
        . El nombre aparece al pasar el mouse sobre cada uno.
      </p>

      <div className="flex flex-col gap-8">
        {ICON_SETS.map((set) => (
          <IconGrid key={set} set={set} />
        ))}
      </div>
    </div>
  </div>
);
